import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  Gauge,
  MapPin,
  Phone,
  Repeat,
} from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Card, CardContent } from "@/components/card";
import { formatCurrency, type Vehicle } from "@/lib/types";
import { cn } from "@/lib/utils";

export function VehicleListing() {
  const { id } = useParams<{ id: string }>();

  const {
    data: vehicle,
    isLoading,
    error,
  } = useQuery<Vehicle>({
    queryKey: ["public-vehicle", id],
    queryFn: async () => {
      const res = await fetch(`/api/public/vehicles/${id}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error("Vehicle not found");
        throw new Error("Failed to fetch vehicle");
      }
      return res.json();
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="aspect-video w-full rounded-2xl bg-slate-200" />
            <div className="h-8 w-64 rounded-lg bg-slate-200" />
            <div className="h-12 w-40 rounded-lg bg-slate-200" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex size-20 items-center justify-center rounded-2xl bg-slate-100">
                <Car className="size-10 text-slate-400" />
              </div>
              <h2 className="mt-4 text-xl font-bold text-slate-900">ไม่พบรถคันนี้</h2>
              <p className="mt-2 text-sm text-slate-500">
                รถคันนี้อาจถูกขายไปแล้วหรือไม่มีอยู่ในระบบ
              </p>
              <Link to="/cars">
                <Button className="mt-6 rounded-xl bg-indigo-500 hover:bg-indigo-600">
                  <ArrowLeft className="mr-2 size-4" />
                  กลับหน้ารถทั้งหมด
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const isAvailable = vehicle.status === "available";

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        {/* Back Link */}
        <Link
          to="/cars"
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          กลับไปหน้ารถทั้งหมด
        </Link>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            {/* Vehicle Image Gallery */}
            <ImageGallery
              images={vehicle.images || []}
              fallbackUrl={vehicle.image_url}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              status={vehicle.status}
            />

            {/* Vehicle Title & Price */}
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h1>
              <p className="mt-2 font-display text-3xl font-bold text-indigo-600 sm:text-4xl">
                {formatCurrency(vehicle.price)}
              </p>
            </div>

            {/* Key Specs */}
            <div className="grid gap-4 sm:grid-cols-3">
              {vehicle.mileage && (
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                      <Gauge className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        เลขไมล์
                      </p>
                      <p className="font-display text-lg font-semibold">
                        {vehicle.mileage.toLocaleString("th-TH")} กม.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
              {vehicle.color && (
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                      <div className="h-5 w-5 rounded-full border-2 border-slate-300 bg-gradient-to-br from-slate-200 to-slate-400" />
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        สี
                      </p>
                      <p className="font-display text-lg font-semibold">{vehicle.color}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
              {vehicle.vin && (
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
                      <span className="text-sm font-bold text-slate-600">#</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        VIN
                      </p>
                      <p className="font-mono text-sm font-medium">{vehicle.vin}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Description */}
            {vehicle.description && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="mb-3 font-display text-lg font-semibold">รายละเอียดรถ</h2>
                  <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground">
                    {vehicle.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Features placeholder */}
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-4 font-display text-lg font-semibold">ทำไมต้องเลือกเรา</h2>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {[
                    "รถมือสองคุณภาพดี",
                    "ราคาโปร่งใส ไม่มีค่าใช้จ่ายแอบแฝง",
                    "ประวัติรถชัดเจน ตรวจสอบได้",
                    "ผ่อนได้ อนุมัติไว",
                    "รับเทิร์นรถเก่า",
                    "รับประกันหลังการขาย",
                  ].map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <Check className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            {/* Contact Card */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 shadow-lg shadow-indigo-700/20 backdrop-blur-sm">
                    <Repeat className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-white">General Auto Cars</h3>
                    <p className="text-xs text-indigo-100">รถมือสองคุณภาพดี</p>
                  </div>
                </div>
              </div>
              <CardContent className="space-y-4 p-5">
                {isAvailable ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      สนใจรถคันนี้? ติดต่อเราวันนี้เพื่อนัดทดลองขับหรือสอบถามรายละเอียดเพิ่มเติม
                    </p>
                    <Button
                      className="w-full rounded-xl bg-indigo-500 hover:bg-indigo-600"
                      size="lg"
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      โทรหาเรา
                    </Button>
                    <Button variant="outline" className="w-full rounded-xl" size="lg">
                      นัดทดลองขับ
                    </Button>
                  </>
                ) : (
                  <div className="rounded-xl bg-muted p-4 text-center">
                    <p className="font-medium text-muted-foreground">รถคันนี้ขายไปแล้ว</p>
                    <Link to="/cars">
                      <Button variant="outline" className="mt-3 rounded-xl" size="sm">
                        ดูรถคันอื่น
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location Card */}
            <Card>
              <CardContent className="flex items-start gap-3 p-5">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50">
                  <MapPin className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-display font-semibold">เยี่ยมชมโชว์รูม</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    123 ถนนพระราม 4
                    <br />
                    กรุงเทพฯ 10110
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    จันทร์-เสาร์: 9:00-19:00 น.
                    <br />
                    อาทิตย์: 10:00-17:00 น.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-border bg-slate-50 py-8">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600">
              <Repeat className="h-4 w-4 text-white" />
            </div>
            <span className="font-display font-bold">General Auto Cars</span>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            แหล่งรวมรถมือสองคุณภาพดี ราคาโดน ไว้วางใจได้
          </p>
          <p className="mt-4 text-xs text-slate-400">
            © {new Date().getFullYear()} General Auto Cars. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/cars" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-sm shadow-indigo-500/20">
              <Repeat className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-display font-bold text-slate-900">General Auto</h1>
              <p className="text-[11px] text-slate-400">รถมือสองคุณภาพดี</p>
            </div>
          </Link>
          <Link to="/cars">
            <Button className="rounded-xl bg-indigo-500 hover:bg-indigo-600" size="sm">
              ดูรถทั้งหมด
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

// Image Gallery Component
interface ImageGalleryProps {
  images: { id: number; image_url: string; sort_order: number; is_primary: boolean }[];
  fallbackUrl: string | null;
  alt: string;
  status: string;
}

function ImageGallery({ images, fallbackUrl, alt, status }: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  // Build the image list - use images array if available, otherwise fallback to single image
  const imageList = React.useMemo(() => {
    if (images && images.length > 0) {
      return images.sort((a, b) => a.sort_order - b.sort_order).map((img) => img.image_url);
    }
    if (fallbackUrl) {
      return [fallbackUrl];
    }
    return [];
  }, [images, fallbackUrl]);

  const hasMultipleImages = imageList.length > 1;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  if (imageList.length === 0) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 shadow-lg">
        <div className="flex aspect-video items-center justify-center">
          <Car className="h-24 w-24 text-slate-300" />
        </div>
        <Badge
          variant={status as "available" | "sold" | "reserved" | "pending"}
          className="absolute right-4 top-4 px-3 py-1.5 text-sm"
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Main Image */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 shadow-lg">
        <img
          src={imageList[currentIndex]}
          alt={`${alt} - รูปที่ ${currentIndex + 1}`}
          className="aspect-video w-full object-cover"
        />

        {/* Status Badge */}
        <Badge
          variant={status as "available" | "sold" | "reserved" | "pending"}
          className="absolute right-4 top-4 px-3 py-1.5 text-sm"
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>

        {/* Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
              aria-label="รูปก่อนหน้า"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
              aria-label="รูปถัดไป"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {hasMultipleImages && (
          <div className="absolute bottom-4 left-4 rounded-full bg-black/50 px-3 py-1 text-sm font-medium text-white">
            {currentIndex + 1} / {imageList.length}
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {hasMultipleImages && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {imageList.map((url, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className={cn(
                "relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all",
                index === currentIndex
                  ? "border-indigo-500 ring-2 ring-indigo-500/20"
                  : "border-transparent opacity-70 hover:opacity-100"
              )}
            >
              <img src={url} alt={`รูปย่อ ${index + 1}`} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
